import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useContext } from 'react';
import type { AppTheme } from '../global';

/**
 * Tema tercihi (Ayarlar > Genel > Görünüm): sistem, açık ya da koyu.
 *
 * Dil sağlayıcısıyla aynı desen: kalıcı değer ana süreçte (settings.json),
 * ilk çizim doğru temada açılsın diye localStorage'da bir kopya. "Sistem"
 * seçiliyken işletim sisteminin teması izleniyor.
 */

const STORAGE_KEY = 'zabit.theme';

function readCachedTheme(): AppTheme {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
  } catch {
    return 'system';
  }
}

function writeCachedTheme(theme: AppTheme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Depolama kullanılamıyorsa kalıcı değer yine ana süreçte.
  }
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return true;
  }
}

function resolveIsDark(theme: AppTheme, systemDark: boolean): boolean {
  if (theme === 'system') return systemDark;
  return theme === 'dark';
}

interface ThemeContextValue {
  theme: AppTheme;
  setTheme: (theme: AppTheme) => Promise<void>;
  isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<AppTheme>(readCachedTheme);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // İşletim sistemi teması değişince "sistem" seçiliyken arayüz de değişsin.
  useEffect(() => {
    let media: MediaQueryList;
    try {
      media = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  // Kalıcı değer ana süreçte; önbellek ondan farklıysa ona uy.
  useEffect(() => {
    let alive = true;
    // Promise.resolve: köprü hazır değilse senkron hata çizimi bozmasın.
    void Promise.resolve()
      .then(() => window.zabit.getAppPrefs())
      .then((prefs) => {
        if (!alive) return;
        setThemeState((current) => {
          if (prefs.theme === current) return current;
          writeCachedTheme(prefs.theme);
          return prefs.theme;
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const setTheme = useCallback(async (next: AppTheme) => {
    setThemeState((previous) => {
      if (previous !== next) writeCachedTheme(next);
      return next;
    });
    try {
      await window.zabit.saveAppPrefs({ theme: next });
    } catch {
      // Kaydedilemese bile arayüz seçilen temada kalsın; bir sonraki
      // açılışta ana süreçteki değere dönülür.
    }
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, isDarkMode: resolveIsDark(theme, systemDark) }),
    [theme, setTheme, systemDark]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme, ThemeProvider dışında kullanıldı.');
  return context;
}
